import React, { useEffect, useState } from 'react';
import PlaybookHero from '../../../components/playbooks/PlaybookHero';
import { InformationCircleIcon, AcademicCapIcon, PlayCircleIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import VideoEmbed from '../../../components/playbooks/VideoEmbed';
import PlaybookMetrics from '../../../components/playbooks/PlaybookMetrics';
import { getResourceBySlug } from '../../../lib/db';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';

const SettingBoundariesKeyInfo = () => {
  const [resource, setResource] = useState(null);
  const metrics = [
    {
      type: 'difficulty',
      title: 'Difficulty',
      value: 'Medium - Requires team alignment and clear communication'
    },
    {
      type: 'timeline',
      title: 'Timeline',
      value: '4-8 Hours Prep'
    },
    {
      type: 'size',
      title: 'Team Size',
      value: '10-100+'
    },
    {
      type: 'why',
      title: 'Why Use',
      value: 'As you scale, the \'fire-fighter-fixer\' mode from earlier stages might need to evolve to ensure your team do not become overextended, misaligned or misunderstood.'
    },
    {
      type: 'when',
      title: 'When to Use',
      value: 'Use this playbook when you notice your team is struggling with \'people pleaser\' mode, unclear roles, or when there are questions about the value and function of the HR/People team.'
    }
  ];

  useEffect(() => {
    getResourceBySlug('setting-boundaries').then(setResource);
  }, []);

  return (
    <div>
      <PlaybookHero
        title={resource?.title || "Setting Boundaries"}
        subtitle={resource?.description || "Learn how to establish and maintain healthy boundaries in your organization"}
        category={resource?.category || "Playbook"}
        bannerUrl={resource?.banner_url}
        readingTime={resource?.timeframe || "10 min read"}
        type={resource?.type}
      />
      <div id="key-info" className="prose prose-invert max-w-none">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <InformationCircleIcon className="h-6 w-6 text-primary-light mr-2" />
          Key Info
        </h2>
        {/* This Guide is for you section */}
        <h3 className="text-xl font-semibold mb-4">This Guide is for you if any of the following are true:</h3>
        <ul className="space-y-3 mb-8">
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>You and your team are stuck in 'people pleaser' mode and are stretched thin</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>There's murmurings about your team like: "what do they even do?" / "How does HR/People team spend their time?"</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>You are struggling to articulate the value of your team to the business</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>You are struggling to communicate how you operate so that expectations from other teams on your time are managed</span>
          </li>
        </ul>
        {/* Guide Overview */}
        <h3 className="text-xl font-semibold mb-4">Guide Overview</h3>
        <div className="mb-12">
          <PlaybookMetrics metrics={metrics} />
        </div>
        {/* Watch This section */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <PlayCircleIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">Watch This</h3>
          </div>
          <VideoEmbed src="https://www.loom.com/embed/50ab84702c674c158865ac3d6254d147?sid=6a0e0cdb-c051-4124-a17f-49025047e323" title="Setting Boundaries Overview" />
          <div className="text-center text-gray-400 text-sm mt-3">Walkthrough: How to set effective boundaries for your team</div>
        </div>
        {/* What Is This Content */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <AcademicCapIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What Is This</h3>
          </div>
          <div className="space-y-4">
            <p className="text-lg">
              Setting boundaries does not have to be at odds with being helpful. If you spend most of your time doing favors for everyone else, there's a good chance you're not being effective in your own role.
            </p>
            <p className="text-lg">
              This can be challenging to address, especially if, like most people in People & Culture, you are naturally more of a giver than a taker. However, it's important to find a balance to ensure you're also meeting your own responsibilities effectively and helping your team achieve their own goals too.
            </p>
            <p className="text-lg">
              This playbook outlines why, when, and how to set effective boundaries for your team so that other teams are clear on what they do, how they work, what they can expect from them and what your core accountabilities are.
            </p>
            <PlaybookImage
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/setting-boundaries/cycle.png"
              alt="The cycle of setting and maintaining boundaries in a team"
              caption="The cycle of setting and maintaining boundaries: clarity, communication, embedding, and review."
            />
            <div className="flex items-center font-semibold mt-4 text-lg">
              Let's get into it <span className="ml-2 text-xl">🤘</span>
            </div>
          </div>
        </div>
        {/* What You'll Need Content */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <WrenchScrewdriverIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What You'll Need</h3>
          </div>
          <ul className="space-y-3 pl-5 list-disc">
            <li className="text-gray-300">Nothing special for this one, just time.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SettingBoundariesKeyInfo; 